/**
 * A reCAPTCHA App Check provider that FAILS instead of HANGING.
 *
 * THE BUG (2026-09-24, Anthony on Safari and on a fresh iPhone PWA: "the nav
 * stays, but the page itself doesn't load"; Today spun forever). Once App
 * Check is initialized, the Firestore SDK asks it for a token before EVERY
 * request — even though Firestore itself is UNENFORCED (checked 2026-09-24:
 * firestore.googleapis.com and firebasestorage.googleapis.com both
 * `enforcementMode: UNENFORCED`). App Check 0.8.0 gets that token by waiting
 * on `grecaptcha.ready()`, with NO timeout, and shares the one in-flight
 * request (`exchangeTokenPromise`) between every caller. So when reCAPTCHA
 * never becomes ready — Safari tracking prevention, a home-screen app, a tab
 * that was suspended during a 45-minute lesson while its token expired — the
 * first read waits forever and every read after it queues behind the same
 * promise. No error is ever thrown, which is why nothing reached the console
 * and why only a reload recovered. Reproduced in a headless browser against
 * the live site with reCAPTCHA blocked: zero Firestore requests in 15s,
 * against three immediately with it allowed.
 *
 * THE FIX, and why it is safe: a provider that rejects after `timeoutMs`.
 * App Check turns a provider rejection into a placeholder token plus a logged
 * error (never a throw), and Firestore proceeds with the placeholder — which
 * is correct precisely because Firestore is unenforced. The ENFORCED
 * callables fail the same way they already fail for a low reCAPTCHA score (a
 * 403 the caller surfaces), so nothing that worked stops working. For
 * `cooldownMs` after a stall, calls fail immediately instead of each waiting
 * out the timeout again; the first success clears it. The stalled underlying
 * request is left running, so a reCAPTCHA that wakes up late still completes
 * and later calls get real tokens.
 *
 * If Firestore or Storage enforcement is ever turned ON, revisit this: a
 * placeholder token would then be refused, and the right behaviour becomes a
 * visible error rather than silent success.
 */

export const APPCHECK_TIMEOUT_MS = 4000;
export const APPCHECK_COOLDOWN_MS = 30000;

export function makeStallSafeProvider(BaseProvider, {
	timeoutMs = APPCHECK_TIMEOUT_MS,
	cooldownMs = APPCHECK_COOLDOWN_MS,
	now = () => Date.now(),
	onStall = () => {},
} = {}) {
	return class StallSafeProvider extends BaseProvider {
		async getToken() {
			if (this._stalledUntil && now() < this._stalledUntil) {
				throw new Error('App Check skipped: reCAPTCHA stalled recently');
			}
			let timer;
			const timeout = new Promise((_, reject) => {
				timer = setTimeout(() => {
					this._stalledUntil = now() + cooldownMs;
					onStall();
					reject(new Error(`reCAPTCHA did not respond within ${timeoutMs}ms`));
				}, timeoutMs);
			});
			try {
				const token = await Promise.race([super.getToken(), timeout]);
				this._stalledUntil = 0;
				return token;
			} finally {
				clearTimeout(timer);
			}
		}
	};
}
